import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Eye, ArrowLeft, Tag, User, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Blog List Page
export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [selectedCategory]);
  
  const fetchPosts = async () => {
    try {
      const url = selectedCategory 
        ? `${API_URL}/api/blog/posts?category=${selectedCategory}`
        : `${API_URL}/api/blog/posts`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };
  
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative py-20 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="font-bold text-4xl md:text-5xl text-white uppercase mb-4 tracking-tight">
            Cobra Thai <span className="text-red-600">Blog</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl">
            Expert insights on MMA, Muay Thai, BJJ, fitness, and combat sports in Pune. 
            Your guide to training smarter and achieving your goals.
          </p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-zinc-500 text-lg">No blog posts yet. Check back soon!</p>
              </div>
            ) : (
              <div className="grid gap-8">
                {posts.map(post => (
                  <Link 
                    key={post.id} 
                    to={`/blog/${post.slug}`}
                    className="group bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden hover:border-red-600/50 transition-all"
                  >
                    {post.featured_image && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={post.featured_image.startsWith('/') ? `${API_URL}${post.featured_image}` : post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-zinc-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(post.published_at).toLocaleDateString('en-IN', { 
                            day: 'numeric', month: 'short', year: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {post.estimated_read_time} min read
                        </span>
                        {post.category && (
                          <span className="px-2 py-0.5 bg-red-600/20 text-red-400 rounded text-xs">
                            {post.category}
                          </span>
                        )}
                      </div>
                      <h2 className="font-bold text-xl text-white mb-2 group-hover:text-red-500 transition-colors leading-snug">
                        {post.title}
                      </h2>
                      <p className="text-zinc-400 line-clamp-2">{post.excerpt}</p>
                      <div className="mt-4 flex items-center text-red-500 text-sm font-medium">
                        Read More <ChevronRight size={16} />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="lg:w-80 space-y-6">
            {/* Categories */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
              <h3 className="font-bold text-lg text-white mb-4">Categories</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded transition-colors ${
                    !selectedCategory ? 'bg-red-600 text-white' : 'text-zinc-400 hover:bg-zinc-800'
                  }`}
                >
                  All Posts
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.name}
                    onClick={() => setSelectedCategory(cat.name)}
                    className={`w-full text-left px-3 py-2 rounded transition-colors flex justify-between ${
                      selectedCategory === cat.name ? 'bg-red-600 text-white' : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className="text-sm opacity-60">{cat.count}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* CTA */}
            <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-lg p-6 text-center">
              <h3 className="font-bold text-xl text-white mb-2">Ready to Train?</h3>
              <p className="text-red-100 text-sm mb-4">
                Experience world-class MMA, Muay Thai, BJJ, and fitness training in Pune.
              </p>
              <a 
                href="https://cobra-thai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-2 bg-white text-red-600 font-bold rounded hover:bg-zinc-100 transition-colors"
              >
                Book FREE Trial
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

// Single Blog Post Page
export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  
  useEffect(() => {
    fetchPost();
    
    // Track blog visit for lead attribution
    if (slug) {
      localStorage.setItem('last_blog_visit', JSON.stringify({
        slug: slug,
        timestamp: Date.now()
      }));
    }
  }, [slug]);
  
  const fetchPost = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/posts/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setPost(data);
        // Fetch related posts
        fetchRelatedPosts(data.category);
      } else {
        setPost(null);
      }
    } catch (err) {
      console.error('Failed to fetch post:', err);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRelatedPosts = async (category) => {
    try {
      const res = await fetch(`${API_URL}/api/blog/posts?category=${category}&limit=3`);
      if (res.ok) {
        const data = await res.json();
        setRelatedPosts(data.posts.filter(p => p.slug !== slug).slice(0, 3));
      }
    } catch (err) {
      console.error('Failed to fetch related posts:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="font-bold text-3xl text-white mb-4">Post Not Found</h1>
          <p className="text-zinc-400 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-red-500 hover:text-red-400 flex items-center justify-center gap-2">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero */}
      <div className="relative py-16 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/blog" className="text-red-500 hover:text-red-400 flex items-center gap-2 mb-6">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
          
          <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {new Date(post.published_at).toLocaleDateString('en-IN', { 
                day: 'numeric', month: 'long', year: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {post.estimated_read_time} min read
            </span>
            <span className="flex items-center gap-1">
              <Eye size={14} />
              {post.views} views
            </span>
          </div>
          
          <h1 className="font-bold text-4xl md:text-5xl text-white mb-4 tracking-tight leading-tight">{post.title}</h1>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-zinc-400">
              <User size={16} />
              <span>{post.author}</span>
            </div>
            {post.category && (
              <span className="px-3 py-1 bg-red-600/20 text-red-400 rounded-full text-sm">
                {post.category}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Featured Image */}
      {post.featured_image && (
        <div className="max-w-5xl mx-auto px-4 -mt-4">
          <img 
            src={post.featured_image.startsWith('/') ? `${API_URL}${post.featured_image}` : post.featured_image}
            alt={post.title}
            className="w-full rounded-lg shadow-2xl"
          />
        </div>
      )}
      
      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg prose-invert prose-red max-w-none
          prose-headings:font-semibold prose-headings:text-white prose-headings:tracking-normal
          prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:font-bold
          prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:font-semibold
          prose-p:text-zinc-300 prose-p:leading-relaxed prose-p:mb-5 prose-p:font-normal
          prose-a:text-red-500 prose-a:no-underline hover:prose-a:text-red-400
          prose-strong:text-white prose-strong:font-semibold
          prose-ul:text-zinc-300 prose-ol:text-zinc-300 prose-ul:my-4
          prose-li:marker:text-red-500 prose-li:mb-2
          prose-blockquote:border-red-600 prose-blockquote:text-zinc-400
          prose-code:text-red-400 prose-code:bg-zinc-800 prose-code:px-1 prose-code:rounded
          prose-hr:border-zinc-700 prose-hr:my-10
        ">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>
        
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-8 pt-8 border-t border-zinc-800">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={16} className="text-zinc-500" />
              {post.tags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-zinc-800 text-zinc-400 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>
      
      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 py-12 border-t border-zinc-800">
          <h2 className="font-bold text-2xl text-white mb-6">Related Articles</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {relatedPosts.map(relPost => (
              <Link 
                key={relPost.id}
                to={`/blog/${relPost.slug}`}
                className="group bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden hover:border-red-600/50 transition-all"
              >
                <div className="p-4">
                  <p className="text-xs text-zinc-500 mb-2">
                    {new Date(relPost.published_at).toLocaleDateString()}
                  </p>
                  <h3 className="font-medium text-white group-hover:text-red-500 transition-colors line-clamp-2">
                    {relPost.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default BlogList;
